import { Check } from 'lucide-react';
import type { Permission } from '@/constants/permissions';
import { cn } from '@/lib/utils';

/** How much of one feature a role holds. `custom` is any mix the switch cannot draw. */
export type FeatureAccessLevel = 'none' | 'view' | 'full' | 'custom';

/** The keys that only read — the rest of a feature's keys change something. */
function viewKeysOf(permissions: readonly Permission[]): Permission[] {
  return permissions.filter((permission) => permission.endsWith('_VIEW'));
}

export function accessLevelOf(
  permissions: readonly Permission[],
  chosen: ReadonlySet<Permission>,
): FeatureAccessLevel {
  const held = permissions.filter((permission) => chosen.has(permission));
  if (held.length === 0) return 'none';
  if (held.length === permissions.length) return 'full';
  const viewKeys = viewKeysOf(permissions);
  const isViewOnly =
    held.length === viewKeys.length && held.every((permission) => viewKeys.includes(permission));
  return isViewOnly ? 'view' : 'custom';
}

export interface FeatureAccessRowProps {
  title: string;
  permissions: readonly Permission[];
  chosen: ReadonlySet<Permission>;
  /** Keys that stay granted whatever is chosen — the Administrator's role keys. */
  locked?: readonly Permission[];
  /**
   * The feature's keys this role may hold, from its kind (`ROLE_KIND_PERMISSIONS`).
   * Omitted: all of them. A subset turns "full access" into everything the
   * kind allows — for a Teacher role on the timetable, that is view only.
   */
  grantable?: readonly Permission[];
  /**
   * Why the row cannot be given at all, shown beside its title — "Administrator
   * only", "Not for teacher roles". Set when `grantable` is empty.
   */
  unavailableReason?: string;
  /** The feature's keys the role should now hold; the caller replaces the old ones. */
  onChange: (granted: Permission[]) => void;
}

/**
 * One feature, one decision: give it to the role or not, and if so whether the
 * job only needs to look or also needs to change things.
 *
 * Replaces a checkbox per key. Thirty-odd keys asked an administrator to know
 * that "Collect fees" and "Manage fee structure" are separate locks; a feature
 * and a level is the question they actually have. A feature with nothing to
 * split — the notice board, messaging — gets the tick alone.
 *
 * A role saved before this screen, or set by a preset, may hold a mix neither
 * level describes (fees collected but not configured). That shows as "Custom"
 * and is left exactly as it is until somebody picks a level.
 */
export function FeatureAccessRow({
  title,
  permissions,
  chosen,
  locked = [],
  grantable,
  unavailableReason,
  onChange,
}: FeatureAccessRowProps) {
  // Measured against what this role may hold, so a Teacher role with view-only
  // timetable reads as fully given rather than "Custom".
  const offered = grantable ?? permissions;
  const isUnavailable = Boolean(unavailableReason) || offered.length === 0;
  const level = accessLevelOf(offered.length > 0 ? offered : permissions, chosen);
  const isOn = level !== 'none';
  const viewKeys = viewKeysOf(offered);
  const canSplit = viewKeys.length > 0 && viewKeys.length < offered.length;
  const isNarrowed = !isUnavailable && offered.length < permissions.length;
  const isLocked = permissions.every((permission) => locked.includes(permission));
  const isDisabled = isLocked || isUnavailable;
  const name = `access-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  const choose = (next: 'none' | 'view' | 'full') => {
    if (next === 'full') onChange([...offered]);
    else if (next === 'view')
      onChange([...new Set([...viewKeys, ...locked.filter((key) => permissions.includes(key))])]);
    else onChange(permissions.filter((permission) => locked.includes(permission)));
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-2xl border px-3.5 py-3 transition-colors sm:flex-row sm:items-center sm:justify-between',
        isUnavailable && 'opacity-60',
        isOn
          ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/25'
          : 'border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e]',
      )}
    >
      <label
        className={cn(
          'flex min-w-0 items-center gap-2.5',
          isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <input
          type="checkbox"
          checked={isOn}
          disabled={isDisabled}
          onChange={(event) => choose(event.target.checked ? 'full' : 'none')}
          className="peer sr-only"
        />
        <span
          className={cn(
            'grid h-5 w-5 shrink-0 place-items-center rounded-md border transition-all',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500/50',
            isOn
              ? 'border-blue-600 bg-gradient-to-br from-blue-500 to-indigo-600 text-white'
              : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900',
          )}
          aria-hidden
        >
          {isOn && <Check className="h-3 w-3" strokeWidth={4} />}
        </span>
        <span
          className={cn(
            'truncate text-[13px] font-bold',
            isOn ? 'text-blue-800 dark:text-blue-200' : 'text-slate-700 dark:text-slate-200',
          )}
        >
          {title}
        </span>
        {isLocked && (
          <span className="shrink-0 text-[10.5px] font-medium text-slate-500 dark:text-slate-400">
            Always on for this role
          </span>
        )}
        {isUnavailable && (
          <span className="shrink-0 text-[10.5px] font-medium text-slate-500 dark:text-slate-400">
            {unavailableReason}
          </span>
        )}
        {isNarrowed && !canSplit && (
          <span className="shrink-0 text-[10.5px] font-medium text-slate-500 dark:text-slate-400">
            View only for this role
          </span>
        )}
      </label>

      {isOn && canSplit && !isDisabled && (
        <div
          role="radiogroup"
          aria-label={`${title} access`}
          className="flex shrink-0 items-center gap-1 self-start rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-0.5 sm:self-auto"
        >
          {(
            [
              { value: 'view', label: 'View only' },
              { value: 'full', label: 'Full access' },
            ] as const
          ).map((option) => (
            <label key={option.value} className="cursor-pointer">
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={level === option.value}
                onChange={() => choose(option.value)}
                className="peer sr-only"
              />
              <span
                className={cn(
                  'block rounded-lg px-2.5 py-1 text-[11.5px] font-bold transition-colors',
                  'peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500/50',
                  level === option.value
                    ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800',
                )}
              >
                {option.label}
              </span>
            </label>
          ))}
          {level === 'custom' && (
            <span className="px-2 text-[10.5px] font-semibold text-amber-600 dark:text-amber-400">
              Custom
            </span>
          )}
        </div>
      )}
    </div>
  );
}
